import {
    TyrePageState,
    ReducerActionNames,
    TyrePageErrors,
    TyreProcessStages,
    TyreRepairRequest
} from './TyreRepairRequestTypes';
import {
    AddressType,
    DealerAddressType,
    UploadedDocument,
    UploadedImage
} from '../../common/CommonTypes';
import { UploadImageTypes } from '../../../Constants/Common';
import { SelectInputHelper } from '../../common/selectInput/SelectInput';

//Please add additional types with | 'your type'
type Action =
    | { type: 'set-tyre-type'; tyreType: string }
    | { type: 'update-tyre-errors'; tyreErrors: TyrePageErrors }
    | { type: 'update-mileage'; mileage: string | number }
    | { type: 'update-incident-date'; dateOfIncident: string }
    | { type: 'update-uploaded-images'; uploadedImages: UploadedImage[] }
    | { type: 'update-police-report'; report: UploadedDocument }
    | {
          type: 'update-tyre-repair-request';
          key: keyof TyreRepairRequest;
          value: string;
      }
    | { type: 'start-new-tyre-request' }
    | { type: 'fetch-current-tyre-request' }
    | { type: 'update-tyre-stage'; newStage: TyreProcessStages }
    | { type: 'add-tyre-request' }
    | { type: 'set-policy-data'; address: AddressType }
    | { type: 'set-repair-address-selected'; value: number }
    | { type: 'set-dealer-address'; address: DealerAddressType }
    | { type: 'set-default-dealer-address'; address: AddressType }
    | { type: 'set-has-errors'; hasErrors: boolean }
    | { type: 'set-permission-granted'; permissionGranted: boolean }
    | {
          type: 'remove-selected-tyre';
          tyre: string;
          newStage: TyreProcessStages;
      }
    | { type: 'set-tyre'; tyre: string; productReference: string }
    | { type: 'view-tyre'; tyre: string; newStage: TyreProcessStages }
    | { type: 'update-tyre'; updateTyre: string }
    | { type: 'scroll-to-address'; scrollToAddress: boolean }
    | { type: 'show-alert'; showCancelAlert: boolean }
    | {
          type: 'set-loading-request-submission';
          loadingOnRequestSubmission: boolean;
      };

const TyreRepairRequestReducer = (
    state: TyrePageState,
    action: Action
): TyrePageState => {
    switch (action.type) {
        case ReducerActionNames.SelectTyreType:
            return {
                ...state,
                tyreType: action.tyreType
            };

        case ReducerActionNames.UpdateTyreErrors:
            return {
                ...state,
                tyreFormErrors: action.tyreErrors
            };
        case ReducerActionNames.SetHasErrors:
            return {
                ...state,
                hasErrors: action.hasErrors
            };
        case ReducerActionNames.UpdateMileage:
            return {
                ...state,
                mileage: action.mileage
            };
        case ReducerActionNames.UpdateIncidentDate:
            return {
                ...state,
                dateOfIncident: action.dateOfIncident,
                currentRepairRequest: {
                    ...state.currentRepairRequest,
                    dateOfIncident: action.dateOfIncident
                }
            };
        case ReducerActionNames.UpdateTyreRepairRequest: {
            const updatedCurrentRepairRequest = {
                ...state.currentRepairRequest,
                [action.key]: action.value
            };

            const updatedRepairRequests = state.tyreRepairRequests.map(
                (repairRequest, index) => {
                    if (index !== state.repairRequestSelectedIndex) {
                        return repairRequest;
                    } else {
                        return updatedCurrentRepairRequest;
                    }
                }
            );

            return {
                ...state,
                currentRepairRequest: updatedCurrentRepairRequest,
                tyreRepairRequests: updatedRepairRequests
            };
        }
        case ReducerActionNames.UpdatePoliceReport: {
            return {
                ...state,
                currentRepairRequest: {
                    ...state.currentRepairRequest,
                    policeReport: action.report
                }
            };
        }
        case ReducerActionNames.UpdateUploadedImages: {
            return {
                ...state,
                currentRepairRequest: {
                    ...state.currentRepairRequest,
                    uploadedImages: action.uploadedImages
                }
            };
        }
        case ReducerActionNames.UpdateTyreStage: {
            return {
                ...state,
                tyreStage: action.newStage
            };
        }
        case ReducerActionNames.SetPolicyData: {
            return {
                ...state,
                repairAddresses: [...state.repairAddresses, action.address]
            };
        }
        case ReducerActionNames.UpdateRepairAddressSelected: {
            return {
                ...state,
                repairAddressSelectedIndex: action.value
            };
        }
        case ReducerActionNames.UpdateDealerAddress: {
            return {
                ...state,
                dealerAddress: action.address
            };
        }
        case ReducerActionNames.SetPermissionGranted: {
            return {
                ...state,
                permissionGranted: action.permissionGranted
            };
        }
        case ReducerActionNames.RemoveTyre: {
            const newSelectedTyres = state.selectedTyres.filter(
                (tyre) => tyre !== action.tyre
            );

            const newTyreRepairRequests = state.tyreRepairRequests.filter(
                (rr) => rr.damagedTyre !== action.tyre
            );

            const repairRequestIndex = newTyreRepairRequests.length - 1;

            return {
                ...state,
                selectedTyres: newSelectedTyres,
                tyreRepairRequests: newTyreRepairRequests,
                repairRequestSelectedIndex: repairRequestIndex,
                currentRepairRequest: newTyreRepairRequests[repairRequestIndex],
                tyreStage: action.newStage
            };
        }
        case ReducerActionNames.SetTyre: {
            const repairRequestIndex = state.tyreRepairRequests.findIndex(
                (rr) => rr.damagedTyre === action.tyre
            );

            if (repairRequestIndex > -1) {
                if (repairRequestIndex === state.repairRequestSelectedIndex) {
                    return {
                        ...state,
                        tyreFormErrors: {}
                    };
                }

                return {
                    ...state,
                    tyreFormErrors: {},
                    repairRequestSelectedIndex: repairRequestIndex,
                    currentRepairRequest:
                        state.tyreRepairRequests[repairRequestIndex]
                };
            } else {
                return createNewRepairRequest(
                    state,
                    action.tyre,
                    action.productReference
                );
            }
        }
        case ReducerActionNames.AddTyreRequest: {
            const newSelectedTyres = state.currentRepairRequest.damagedTyre;
            const repairRequestIndex = state.tyreRepairRequests.findIndex(
                (rr) => rr.damagedTyre === newSelectedTyres
            );
            const newRepairRequest = state.currentRepairRequest;
            if (repairRequestIndex > -1) {
                return state;
            } else {
                return {
                    ...state,
                    selectedTyres: [...state.selectedTyres, newSelectedTyres],
                    tyreRepairRequests: [
                        ...state.tyreRepairRequests,
                        newRepairRequest
                    ],
                    currentRepairRequest: newRepairRequest
                };
            }
        }
        case ReducerActionNames.ShowCancelAlert: {
            return {
                ...state,
                showCancelAlert: action.showCancelAlert
            };
        }
        case ReducerActionNames.UpdateTyre: {
            return {
                ...state,
                updateTyre: action.updateTyre
            };
        }
        case ReducerActionNames.ScrollToAddress: {
            return {
                ...state,
                scrollToAddress: action.scrollToAddress
            };
        }
        case ReducerActionNames.LoadingOnRequestSubmission: {
            return {
                ...state,
                isLoadingOnSubmission: action.loadingOnRequestSubmission
            };
        }
        default:
            return state;
    }
};

const createNewRepairRequest = (
    state: TyrePageState,
    tyre: string,
    productReference: string
): TyrePageState => {
    const newIndex = state.tyreRepairRequests.length;

    const newRepairRequest = {
        id: newIndex,
        productReference: productReference,
        dateOfIncident: undefined,
        damagedTyre: tyre,
        damageType: SelectInputHelper.NullValue(),
        damageSize: SelectInputHelper.NullValue(),
        additionalInformation: '',
        uploadedImages: [
            {
                type: UploadImageTypes.CloseUp,
                uploadedPath: ''
            },
            {
                type: UploadImageTypes.Distance,
                uploadedPath: ''
            },
            {
                type: UploadImageTypes.AdditionalImage1,
                uploadedPath: ''
            },
            {
                type: UploadImageTypes.AdditionalImage2,
                uploadedPath: ''
            }
        ],
        repairOption: SelectInputHelper.NullValue()
    };

    return {
        ...state,
        tyreFormErrors: {},
        repairRequestSelectedIndex: newIndex,
        currentRepairRequest: newRepairRequest
    };
};

export default TyreRepairRequestReducer;
